import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { mcpRestClient } from '@/lib/mcp/restClient';
import type { BaseTool, CustomTool, BuildFromSourcePayload } from '@/lib/mcp/types';
import { toUserMessage } from '@/lib/api/errors';

export const useMcpToolsStore = defineStore('mcpTools', () => {
  // State
  const baseTools = ref<BaseTool[]>([]);
  const customTools = ref<CustomTool[]>([]);
  const enabledToolIds = ref<Set<string>>(new Set());
  const isLoadingBaseTools = ref(false);
  const isLoadingCustomTools = ref(false);
  const baseToolsError = ref<string | null>(null);
  const customToolsError = ref<string | null>(null);

  // Getters
  const enabledBaseTools = computed(() =>
    baseTools.value.filter((t) => enabledToolIds.value.has(t.id)),
  );

  const disabledBaseTools = computed(() =>
    baseTools.value.filter((t) => !enabledToolIds.value.has(t.id)),
  );

  const toolById = computed(() => (id: string): BaseTool | CustomTool | undefined => {
    return (
      baseTools.value.find((t) => t.id === id) ??
      customTools.value.find((t) => t.id === id)
    );
  });

  const isNativeTool = computed(() => (tool: BaseTool | CustomTool): boolean => {
    return !tool.wasm_ref;
  });

  // Actions
  const fetchBaseTools = async (orgId: string): Promise<void> => {
    isLoadingBaseTools.value = true;
    baseToolsError.value = null;
    try {
      const [tools, enabledIds] = await Promise.all([
        mcpRestClient.listBaseTools(orgId),
        mcpRestClient.listEnabledToolIds(orgId).catch(() => [] as string[]),
      ]);
      baseTools.value = tools;
      enabledToolIds.value = new Set(enabledIds);
    } catch (e) {
      baseToolsError.value = toUserMessage(e, 'Failed to load base tools');
    } finally {
      isLoadingBaseTools.value = false;
    }
  };

  const fetchCustomTools = async (orgId: string): Promise<void> => {
    isLoadingCustomTools.value = true;
    customToolsError.value = null;
    try {
      customTools.value = await mcpRestClient.listCustomTools(orgId);
    } catch (e) {
      customToolsError.value = toUserMessage(e, 'Failed to load custom tools');
    } finally {
      isLoadingCustomTools.value = false;
    }
  };

  const enableTool = async (
    orgId: string,
    toolId: string,
    toolType: 'base' | 'custom' = 'base',
  ): Promise<void> => {
    await mcpRestClient.enableTool(orgId, toolId, toolType);
    enabledToolIds.value = new Set([...enabledToolIds.value, toolId]);
  };

  const disableTool = async (orgId: string, toolId: string): Promise<void> => {
    await mcpRestClient.disableTool(orgId, toolId);
    const next = new Set(enabledToolIds.value);
    next.delete(toolId);
    enabledToolIds.value = next;
  };

  const createCustomTool = async (
    orgId: string,
    payload: Partial<CustomTool>,
  ): Promise<CustomTool> => {
    const created = await mcpRestClient.createCustomTool(orgId, payload);
    customTools.value = [...customTools.value, created];
    return created;
  };

  const updateCustomTool = async (
    orgId: string,
    toolId: string,
    payload: Partial<CustomTool>,
  ): Promise<CustomTool> => {
    const updated = await mcpRestClient.updateCustomTool(orgId, toolId, payload);
    customTools.value = customTools.value.map((t) => (t.id === toolId ? updated : t));
    return updated;
  };

  const deleteCustomTool = async (orgId: string, toolId: string): Promise<void> => {
    await mcpRestClient.deleteCustomTool(orgId, toolId);
    customTools.value = customTools.value.filter((t) => t.id !== toolId);
  };

  const buildFromSource = async (
    orgId: string,
    payload: BuildFromSourcePayload,
  ): Promise<{ build_id: string }> => {
    return mcpRestClient.buildFromSource(orgId, payload);
  };

  return {
    // State
    baseTools,
    customTools,
    enabledToolIds,
    isLoadingBaseTools,
    isLoadingCustomTools,
    baseToolsError,
    customToolsError,
    // Getters
    enabledBaseTools,
    disabledBaseTools,
    toolById,
    isNativeTool,
    // Actions
    fetchBaseTools,
    fetchCustomTools,
    enableTool,
    disableTool,
    createCustomTool,
    updateCustomTool,
    deleteCustomTool,
    buildFromSource,
  };
});
